import "./App.css";
import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Home from "./components/Home";
import Layout from "./components/Layout";
import NoFound from "./components/NoFound";
import MaintenancePage from "./components/MaintenancePage";
import AdminLogin from "./components/AdminLogin";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./context/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import Unauthorized from "./context/Unauthorized";
import { TenantConfigProvider, useTenantConfig } from "./context/TenantConfigContext";

/* -------------------------------------------
   LAZY LOADED ADMIN ROUTES (Heavy Components)
-------------------------------------------- */
const AdminLayout = React.lazy(() => import("./components/AdminLayout"));
const BulkUpload = React.lazy(() => import("./components/BulkFieldUpdate"));
const ManageUsers = React.lazy(() => import("./components/ManageUsers"));
const BatchFieldPage = React.lazy(() => import("./components/BatchFields"));
const UtilitiesPage = React.lazy(() => import("./components/Utilities"));
const BatchPage = React.lazy(() => import("./components/batches/Batches"));
const BatchFieldCategoryPage = React.lazy(() => import("./components/BatchFieldCategory"));
const BatchImport = React.lazy(() => import("./components/BatchImport"));
const WebhookPage = React.lazy(() => import("./components/loanevents/WebhookPage"));
const Audit = React.lazy(() => import("./components/audit/TimelineView"));
const PollyWebhookPage = React.lazy(() => import("./components/pollyevents/PollyWebhookPage"));
const LoansnapshotPage = React.lazy(() => import("./components/milestoneevents/MilestoneEventsPage"));
const ConditionSnapshotPage = React.lazy(() => import("./components/conditionsnapshot/conditionsnapshot"));
const Tasks = React.lazy(() => import("./components/LoanTasks"));
const EncompassFieldUpdate = React.lazy(() => import("./components/LosBulkFieldUpdate"));
const Reprocessing = React.lazy(() => import("./components/deadletterprocessing/reprocessing"));
const AttachmentReprocessing = React.lazy(() => import("./components/deadletterprocessing/attachmentreprocessing"));
const HouseCanary = React.lazy(() => import("./components/housecanary/hcapi"));
const RolePermission = React.lazy(() => import("./components/RolePermissionMapping"));
const SiteXPRO = React.lazy(() => import("./components/siteXPro/sitexpro"));
const ConsolidatedPropertyView = React.lazy(() => import("./components/consolidatedpropertyview/ConsolidatedPropertyView"));
const AutoLoanSubmission = React.lazy(() => import("./components/autoloansubmission/autoloansubmission"));
const LoanFolderMovement = React.lazy(() => import("./components/loanfoldermovement/loanfoldermovement"));
const AutomatedCUReview = React.lazy(() => import("./components/automatedcureview/automatedcureview"));
const EncompassIntegration = React.lazy(() => import("./components/encompassintegration/EncompassIntegration"));
const RulesAdmin = React.lazy(() => import("./components/rulesadmin/RulesAdmin"));
const RuleEngineVersion = React.lazy(() => import("./components/RuleEngineVersion/RulesAdmin"));
const RuleFields = React.lazy(() => import("./components/rulesadmin/RuleFields"));
const RuleExecutionResults = React.lazy(() => import("./components/rulesadmin/RuleExecutionResults"));
const OrgConfiguration = React.lazy(() => import("./components/orgconfiguration/orgconfig"));
const DocReview = React.lazy(() => import("./components/documentreview/recommendeddocreview"));
const Dashboard = React.lazy(() => import("./components/dashboard/Dashboard"));
const RuleEngineDashboard = React.lazy(() => import("./components/ruleengine/RuleEngineDashboard"));
const RuleEngineSection = React.lazy(() => import("./components/ruleengine/RuleEngineSection"));

const DEFAULT_APP_TITLE = "Champions Funding";
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ""}/images/champs/favicon.png`;
const DEFAULT_THEME_PRIMARY_COLOR = "#ff0f63";
const DEFAULT_THEME_PRIMARY_HOVER_COLOR = "#e50956";
const DEFAULT_THEME_PRIMARY_SOFT_COLOR = "#ffe6ec";
const DEFAULT_THEME_PRIMARY_TEXT_COLOR = "#ffffff";

const isValidCssColor = (value) => {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  if (typeof window === "undefined" || !window.CSS || !window.CSS.supports) {
    return true;
  }

  return window.CSS.supports("color", value.trim());
};

const toRgbTuple = (hexColor, fallbackTuple) => {
  if (typeof hexColor !== "string") {
    return fallbackTuple;
  }

  const normalized = hexColor.trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return fallbackTuple;
  }

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);

  return `${red}, ${green}, ${blue}`;
};

const applyTenantTheme = (tenantConfig) => {
  const root = document.documentElement;
  const primaryColorCandidate =
    tenantConfig?.themePrimaryColor ||
    tenantConfig?.primaryColor ||
    tenantConfig?.brandColor ||
    tenantConfig?.buttonColor ||
    tenantConfig?.accentColor;
  const hoverColorCandidate =
    tenantConfig?.themePrimaryHoverColor ||
    tenantConfig?.primaryHoverColor ||
    tenantConfig?.brandHoverColor ||
    tenantConfig?.buttonHoverColor;
  const softColorCandidate =
    tenantConfig?.themePrimarySoftColor ||
    tenantConfig?.primarySoftColor ||
    tenantConfig?.brandSoftColor ||
    tenantConfig?.buttonSoftColor;
  const textColorCandidate =
    tenantConfig?.themePrimaryTextColor ||
    tenantConfig?.primaryTextColor ||
    tenantConfig?.brandTextColor ||
    tenantConfig?.buttonTextColor;

  const primaryColor = isValidCssColor(primaryColorCandidate)
    ? primaryColorCandidate.trim()
    : DEFAULT_THEME_PRIMARY_COLOR;
  const hoverColor = isValidCssColor(hoverColorCandidate)
    ? hoverColorCandidate.trim()
    : DEFAULT_THEME_PRIMARY_HOVER_COLOR;
  const softColor = isValidCssColor(softColorCandidate)
    ? softColorCandidate.trim()
    : DEFAULT_THEME_PRIMARY_SOFT_COLOR;
  const textColor = isValidCssColor(textColorCandidate)
    ? textColorCandidate.trim()
    : DEFAULT_THEME_PRIMARY_TEXT_COLOR;
  const primaryRgb = toRgbTuple(primaryColor, "255, 15, 99");

  root.style.setProperty("--tenant-primary-color", primaryColor);
  root.style.setProperty("--tenant-primary-hover-color", hoverColor);
  root.style.setProperty("--tenant-primary-soft-color", softColor);
  root.style.setProperty("--tenant-primary-text-color", textColor);
  root.style.setProperty("--tenant-primary-rgb", primaryRgb);
  root.style.setProperty("--tenant-primary-disabled-color", `rgba(${primaryRgb}, 0.7)`);
};

const upsertHeadLink = (rel, href) => {
  if (!href) return;

  let link = document.querySelector(`link[rel='${rel}']`);

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
};

/* ------------ Global Lazy Loader ------------- */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );
}

function App() {
  return (
    <TenantConfigProvider>
      <TenantAwareApp />
    </TenantConfigProvider>
  );
}

function TenantAwareApp() {
  const { tenantConfig, loading: tenantConfigLoading } = useTenantConfig();

  useEffect(() => {
    const nextTitle =
      tenantConfig?.browserTitle || tenantConfig?.tenantDisplayName || DEFAULT_APP_TITLE;
    const nextIcon =
      tenantConfig?.browserTabIconUrl ||
      tenantConfig?.loginLogoUrl ||
      tenantConfig?.sidebarLogoUrl ||
      DEFAULT_FAVICON;

    document.title = nextTitle;
    upsertHeadLink("icon", nextIcon);
    upsertHeadLink("apple-touch-icon", nextIcon);
    applyTenantTheme(tenantConfig);
  }, [tenantConfig]);

  if (tenantConfigLoading) {
    return <PageLoader />;
  }

  const isMaintenance = process.env.REACT_APP_MAINTENANCE_MODE === "true";

  if (isMaintenance) {
    return (
      <Router>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<MaintenancePage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="*" element={<MaintenancePage />} />
          </Route>
        </Routes>
      </Router>
    );
  }

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

function AppRoutes() {
  const { loading } = useAuth();

  if (loading) return <PageLoader />;

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* PUBLIC ROUTES */}
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<AdminLogin />} />
            <Route path="*" element={<NoFound />} />
          </Route>
          <Route path="/unauthorized" element={<Unauthorized />} />
          {/* PROTECTED WRAPPER */}
          <Route element={<ProtectedRoute />}>

            {/* --- Super Admin + Admin / AdminLayout Wrapper --- */}
            <Route element={<AdminLayout />}>

              <Route path="/admin/dashboard" element={<Dashboard />} />
              <Route path="/admin/fieldupdate" element={<BulkUpload />} />
              <Route path="/admin/hc" element={<HouseCanary />} />
              <Route path="/admin/manageusers" element={<ManageUsers />} />
              <Route path="/admin/batches" element={<BatchPage />} />
              <Route path="/admin/batchfields" element={<BatchFieldPage />} />
              <Route path="/admin/batchfieldcategory" element={<BatchFieldCategoryPage />} />
              <Route path="/admin/batchimport" element={<BatchImport />} />
              <Route path="/admin/utilities" element={<UtilitiesPage />} />
              <Route path="/admin/loanevents" element={<WebhookPage />} />
              <Route path="/admin/timelineview" element={<Audit />} />
              <Route path="/admin/pollyevents" element={<PollyWebhookPage />} />
              <Route path="/admin/loansnapshot" element={<LoansnapshotPage />} />
              <Route path="/admin/conditionsnapshot" element={<ConditionSnapshotPage />} />
              <Route path="/admin/tasks" element={<Tasks />} />
              <Route path="/admin/autoloansubmission" element={<AutoLoanSubmission />} />
              <Route path="/admin/loanfoldermovement" element={<LoanFolderMovement />} />
              <Route path="/admin/automatedcureview" element={<AutomatedCUReview />} />
              <Route path="/admin/encompassintegration" element={<EncompassIntegration />} />
              <Route path="/admin/losfieldupdate" element={<EncompassFieldUpdate />} />
              <Route path="/admin/reprocessing" element={<Reprocessing />} />
              <Route path="/admin/attachmentreprocessing" element={<AttachmentReprocessing />} />
              <Route path="/admin/rolepermission" element={<RolePermission />} />
              <Route path="/admin/sx" element={<SiteXPRO />} />
              <Route path="/admin/consolidatedpropertyview" element={<ConsolidatedPropertyView />} />
              <Route path="/admin/rules" element={<RulesAdmin />} />
              <Route path="/admin/ruleengineversion" element={<RuleEngineVersion />} />
              <Route path="/admin/rulefields" element={<RuleFields />} />
              <Route path="/admin/ruleexecutionresults" element={<RuleExecutionResults />} />
              <Route path="/admin/orgconfig" element={<OrgConfiguration />} />
              <Route path="/admin/docreview" element={<DocReview />} />
              <Route path="/admin/ruleengine/dashboard" element={<RuleEngineDashboard />} />
              <Route path="/admin/ruleengine/normal-rules" element={<RuleEngineSection />} />
              <Route path="/admin/ruleengine/condition-rules" element={<RuleEngineSection />} />
              <Route path="/admin/ruleengine/condition-catalog" element={<RuleEngineSection />} />
              <Route path="/admin/ruleengine/releases" element={<RuleEngineSection />} />
              <Route path="/admin/ruleengine/testing" element={<RuleEngineSection />} />
              <Route path="/admin/ruleengine/operations" element={<RuleEngineSection />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;